/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  useRouteError
} from 'react-router';
import { ROUTE_PREFIX } from '@link/agent/constants';
import { BaseLayout, AgentLayout } from '@link/agent/layouts';
import { SearchActivitiesBookingTypeEnum } from '@link/agent/services/const';
import { Loader } from '@link/share/components';
import { INBOX_ENABLED } from '@link/share/constants';
import {
  NotFound,
  PrePayment,
  PrivacyPolicy,
  LegalNotice
} from '@link/share/views';
import PrivateRoute from './PrivateRoute';

const isProd = import.meta.env.VITE_APP_AGENT_ENV === 'production';

const Dashboard = lazy(() => import('@link/agent/views/Dashboard'));
const BookingList = lazy(() => import('@link/agent/views/Booking/List'));
const BookingDetail = lazy(() => import('@link/agent/views/Booking/Detail'));
const BookingCancel = lazy(() => import('@link/agent/views/Booking/Cancel'));
const BookingDiscrepancies = lazy(
  () => import('@link/agent/views/Booking/Discrepancies')
);
const BookingForm = lazy(() => import('@link/agent/views/Booking/Form'));
const Reservations = lazy(
  () => import('@link/agent/views/Booking/Reservations')
);
const SearchActivities = lazy(
  () => import('@link/agent/views/Activity/SearchActivities')
);
const ActivityDetail = lazy(() => import('@link/agent/views/Activity/Detail'));
const ActivityList = lazy(() => import('@link/agent/views/Activity/List'));
const Login = lazy(() => import('@link/agent/views/Login'));
const PasswordReset = lazy(() => import('@link/agent/views/PasswordReset'));
const ChangePassword = lazy(() => import('@link/agent/views/ChangePassword'));
const ContractsList = lazy(() => import('@link/agent/views/Contracts'));
const AgentDetail = lazy(() => import('@link/agent/views/Agent/AgentDetail'));
const OrgList = lazy(() => import('@link/agent/views/Agent/OrgList'));
const AgentsAccountList = lazy(
  () => import('@link/agent/views/Agent/AccountsList')
);
const AgentsAccountDetail = lazy(
  () => import('@link/agent/views/Agent/AccountDetail')
);
const AgentsOrgDetail = lazy(() => import('@link/agent/views/Agent/OrgDetail'));
const FinanceHistory = lazy(() => import('@link/agent/views/Finance/History'));
const FinanceDiscrepancies = lazy(
  () => import('@link/agent/views/Finance/Discrepancies')
);
const FinanceTransactions = lazy(
  () => import('@link/agent/views/Finance/Transactions')
);
const FinanceAccount = lazy(() => import('@link/agent/views/Finance/Account'));
const FinanceChange = lazy(() => import('@link/agent/views/Finance/Change'));
const FinanceAddPayment = lazy(
  () => import('@link/agent/views/Finance/AddPayment')
);

const BulkOrderBookingForm = lazy(
  () => import('@link/agent/views/BulkOrder/BookingForm')
);
const BulkOrderBookingList = lazy(
  () => import('@link/agent/views/BulkOrder/BookingList')
);
const BulkOrderBookingDetail = lazy(
  () => import('@link/agent/views/BulkOrder/BookingDetail')
);

const NotificationsInbox = lazy(
  () => import('@link/agent/views/Notifications/Inbox')
);

function RootErrorBoundary() {
  const error = useRouteError() as Error;
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    location.reload();
  }
  return null;
}

const lazyView = (view: JSX.Element) => {
  return <Suspense fallback={<Loader />}>{view}</Suspense>;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route element={<PrivateRoute />}>
        <Route path="/" element={<BaseLayout />}>
          <Route
            index
            element={<Navigate to="book/search-activities" replace />}
          />
          <Route path="dashboard" element={lazyView(<Dashboard />)} />
          <Route path="contracts">
            <Route path="list" element={lazyView(<ContractsList />)} />
          </Route>
          <Route path="agent" element={<AgentLayout />}>
            <Route path="detail" element={lazyView(<AgentDetail />)} />
            <Route path="orgs">
              <Route index element={lazyView(<OrgList />)} />
              <Route path="new" element={lazyView(<AgentsOrgDetail />)} />
              <Route
                path=":orgId"
                element={lazyView(<AgentsOrgDetail />)}
                handle={{ hideAgentSelect: true }}
              />
            </Route>
            <Route path="accounts">
              <Route index element={lazyView(<AgentsAccountList />)} />
              <Route path="new" element={lazyView(<AgentsAccountDetail />)} />
              <Route
                path=":accountId/:otaOrgId?"
                element={lazyView(<AgentsAccountDetail />)}
                handle={{ hideAgentSelect: true }}
              />
            </Route>
          </Route>
          {/* 在庫商品一覧：后端未就绪，本番环境暂不开放 */}
          {!isProd && (
            <Route path="activities" element={lazyView(<ActivityList />)} />
          )}
          <Route path="book">
            <Route path="booking" element={lazyView(<BookingList />)} />
            <Route path="booking/:bookingCustomId">
              <Route index element={lazyView(<BookingDetail />)} />
              <Route
                path="discrepancies/:bookingId"
                element={lazyView(<BookingDiscrepancies />)}
              />
              <Route
                path="cancel/:bookingId"
                element={lazyView(<BookingCancel />)}
              />
            </Route>
            <Route path="search-activities">
              <Route index element={lazyView(<SearchActivities />)} />
              <Route path=":activityCustomId">
                <Route index element={lazyView(<ActivityDetail />)} />
                <Route path="form" element={lazyView(<BookingForm />)} />
              </Route>
            </Route>
            <Route path="reservations" element={lazyView(<Reservations />)} />
          </Route>
          {INBOX_ENABLED && (
            <Route path="notifications">
              <Route path="inbox" element={lazyView(<NotificationsInbox />)} />
            </Route>
          )}
          <Route path="finance">
            <Route path="history" element={lazyView(<FinanceHistory />)} />
            <Route
              path="discrepancies"
              element={lazyView(<FinanceDiscrepancies />)}
            />
            <Route
              path="transactions"
              element={lazyView(<FinanceTransactions />)}
            />
            <Route
              path="transactions/:invoiceNumber"
              element={lazyView(<FinanceChange />)}
            />
            <Route path="account" element={lazyView(<FinanceAccount />)} />
            <Route
              path="account/add-payment"
              element={lazyView(<FinanceAddPayment />)}
            />
          </Route>
          <Route path="bulk-order">
            <Route path="list" element={lazyView(<BulkOrderBookingList />)} />
            <Route
              path="list/:bulkOrderId"
              element={lazyView(<BulkOrderBookingDetail />)}
            />
            <Route path="form" element={lazyView(<BulkOrderBookingForm />)} />
            <Route path="search-activities">
              <Route
                index
                element={lazyView(
                  <SearchActivities
                    bookingType={SearchActivitiesBookingTypeEnum.Bulk}
                    routePrefix={ROUTE_PREFIX.BULK_ORDER}
                  />
                )}
              />
              <Route path=":activityCustomId">
                <Route
                  index
                  element={lazyView(
                    <ActivityDetail routePrefix={ROUTE_PREFIX.BULK_ORDER} />
                  )}
                />
                <Route
                  path="form"
                  element={lazyView(<BulkOrderBookingForm />)}
                />
              </Route>
            </Route>
          </Route>
          <Route
            path="/change-password"
            element={lazyView(<ChangePassword />)}
          />
        </Route>
      </Route>
      <Route path="/login" element={lazyView(<Login />)} />
      <Route path="/finance/pre-payment" element={lazyView(<PrePayment />)} />
      <Route path="/reset-password" element={lazyView(<PasswordReset />)} />
      <Route path="/privacy-policy" element={lazyView(<PrivacyPolicy />)} />
      <Route path="/legal-notice" element={lazyView(<LegalNotice />)} />
      <Route path="*" element={lazyView(<NotFound />)} />
    </Route>
  )
);

export default router;
